import { useEffect, useState } from 'react';
import { apiRelatorios, type LinhaDoTempo as Dados, type MarcoNaLinha } from '../lib/api';
import { paraTela } from '../lib/datas';

/**
 * A LINHA DO TEMPO do caso — 08/09/2026.
 *
 * Entrega declarada do **N2 · FORENSIC**: *"linha do tempo de alienações"*.
 *
 * ## ⭐ O que ela mostra que nenhuma outra seção mostra
 *
 * Cada fato do caso mora numa seção diferente — a transmissão em Imóveis, o
 * protesto em Restrições, a escritura em Atos notariais, e as datas da dívida
 * no card do topo. **Ninguém compara mentalmente doze datas espalhadas.**
 *
 * O método pede exatamente essa comparação
 * (`docs/CLASSIFICACAO-PATRIMONIAL.md` §3): *"a proximidade temporal entre
 * transmissão, surgimento da obrigação e ajuizamento deve ser analisada junto
 * com o vínculo entre as partes"*.
 *
 * ⛔ **E ela NÃO conclui.** O mesmo método diz: *"a transmissão, isoladamente,
 * NÃO autoriza conclusão de fraude"*. Aqui os fatos ficam em ordem e os da
 * dívida ficam marcados; **quem lê a proximidade é quem tem OAB**.
 */

/** O rótulo de cada espécie, e a cor sai do CSS pela mesma chave. */
const ESPECIE: Record<MarcoNaLinha['especie'], string> = {
  divida: 'crédito',
  aquisicao: 'entrada',
  saida: 'saída',
  onus: 'ônus',
  passivo: 'passivo',
  ato: 'ato notarial',
  constatacao: 'constatação',
  processo: 'processo',
};

export function LinhaDoTempo({ relatorioId, chave }: { relatorioId: string; chave: string }) {
  const [d, setD] = useState<Dados | null>(null);

  useEffect(() => {
    let vivo = true;
    apiRelatorios
      .linhaDoTempo(relatorioId)
      .then((r) => vivo && setD(r))
      .catch(() => vivo && setD(null));
    return () => {
      vivo = false;
    };
  }, [relatorioId, chave]);

  if (!d) return null;

  /*
   * ⭐ **A JANELA CRÍTICA — do surgimento da obrigação ao ajuizamento.**
   *
   * ⚠️ **Ela MARCA, e não conclui.** É dentro dela que o método manda olhar com
   * atenção — mas um fato ali dentro não é fraude, é fato ali dentro. O rótulo
   * diz "dentro da janela da dívida", nunca "suspeito".
   *
   * ⚠️ **Começa na data mais antiga entre contrato e inadimplência**, porque a
   * obrigação nasce com o contrato e se torna exigível com o inadimplemento —
   * e transmissão entre os dois já interessa.
   */
  const inicio = [d.dividaEm, d.inadimplenciaEm].filter(Boolean).sort()[0] ?? null;
  const fim = d.ajuizadoEm;
  const naJanela = (data: string) =>
    inicio !== null && data >= inicio && (fim === null || data <= fim);

  if (d.marcos.length === 0) {
    return (
      <p className="ajuda ajuda-solta">
        Ainda não há fato com data neste caso.{' '}
        {d.semData > 0 && (
          <>
            <strong>{d.semData} lançamento(s) não têm data</strong> e por isso não entram na
            linha — a data do fato se informa em cada lançamento.
          </>
        )}
      </p>
    );
  }

  return (
    <div className="linha-do-tempo">
      <p className="ajuda">
        Os fatos do caso em ordem, com os marcos do crédito destacados.{' '}
        <strong>A linha ordena; não conclui.</strong> Proximidade entre uma saída de
        patrimônio e as datas da dívida é elemento de análise — não é, por si, prova de nada.
      </p>

      <ol className="marcos">
        {d.marcos.map((m, i) => (
          <li
            key={`${m.data}-${m.titulo}-${i}`}
            className={`marco marco-${m.especie}${naJanela(m.data) ? ' marco-na-janela' : ''}`}
          >
            <span className="marco-data dado">{paraTela(m.data)}</span>
            <span className="marco-corpo">
              <span className="marco-titulo">{m.titulo}</span>
              {m.investigado && <span className="ajuda"> · {m.investigado}</span>}
              {m.detalhe && <span className="marco-detalhe">{m.detalhe}</span>}
            </span>
            <span className={`marco-selo selo-${m.especie}`}>{ESPECIE[m.especie]}</span>
          </li>
        ))}
      </ol>

      {/*
        ⛔ **O que ficou de fora precisa ser dito.** Uma linha do tempo que se
        cala sobre os fatos sem data mente por omissão: quem a lê supõe que ali
        está tudo, e decide sobre uma cronologia incompleta sem saber. É a mesma
        regra do "sem avaliação" no painel de ativos.
      */}
      {d.semData > 0 && (
        <p className="ajuda ressalvas-leitura">
          ⚠️ <strong>{d.semData} lançamento(s) do caso não têm data</strong> e ficam fora
          desta linha. Não é que não tenham acontecido — é que a data não foi informada, e
          sem ela não há onde colocá-los.
        </p>
      )}

      {/*
        ⚠️ **A ausência sai da linha, mas não do conhecimento.** *"Nada consta"*
        é resposta valiosa — separa *consultamos e nada havia* de *não
        consultamos* —, só não é um fato com data. Dizer quantos são impede que
        a linha pareça esconder consultas.
      */}
      {d.ausencias > 0 && (
        <p className="ajuda">
          {d.ausencias} consulta(s) responderam <strong>“nada consta”</strong> e não entram
          na linha: ausência de débito não tem data de acontecimento. Elas continuam no
          capítulo de restrições e passivo.
        </p>
      )}

      {/* ⭐ 27/10/2026: os imóveis que não são das partes saem da linha, e ela diz quantos (decisão dele). */}
      {(d.imoveisDeFora ?? 0) > 0 && (
        <p className="ajuda">
          {d.imoveisDeFora} imóvel(is) em nome de terceiro ou com a matrícula encerrada não entram
          na linha: ela acompanha o patrimônio das partes. Eles continuam no capítulo de imóveis.
        </p>
      )}

      {inicio && (
        <p className="ajuda">
          A faixa destacada vai de <strong>{paraTela(inicio)}</strong>
          {fim ? (
            <>
              {' '}até <strong>{paraTela(fim)}</strong> — do surgimento da obrigação ao
              ajuizamento.
            </>
          ) : (
            <> em diante — a ação ainda não foi ajuizada, ou a data não foi informada.</>
          )}
        </p>
      )}
    </div>
  );
}
